import $ from "jquery";
import Spinner from "../../spin.min";
import BoolTimer from "../../util/BoolTimer";
import messageResource from "../../messageResource";
import StringIndex from "../../util/StringIndex";
import KeyDownHandler from "../../key_down_handler";
import TokensHighlighter from "../../util/TokensHighlighter";
import {setMouseDownOutsideHandler} from "../../atBrowserUtils";
import {KEY_CODE_ENTER, KEY_CODE_ESC, KEY_CODE_UP, KEY_CODE_DOWN} from "../../keyCodes";

/**
 * @author kostenko
 *
 * A controller for customers & projects hierarchical selector.
 *
 * Requires:
 *   jQuery
 *   jQuery.scrollintoview
 *   /js/at.js
 *   /js/message_resources.js (using 'submit_tt' prefixes)
 *   /js/spin.min.js
 *
 * @param container - the element which contains all the needed markup (a DOM element, jQuery object or selector)
 *                    (for example of markup see 'customerProjectSelector' element in task_select_control.jsp)
 * @param onCustomerOrProjectSelect - function( customerId, projectId ) executed when the user selects a customer or a project.
 *                    Note: customerId is 0 if a project is selected, and projectId is 0 if a customer is selected.
 * @constructor
 *
 * The following methods are declared:
 *   init
 *   isInitialized
 *   spin
 *   stopSpin
 *   closeDropdown
 * See their declaration in the constructor body for comments.
 */
function CPSelectorController( container, onCustomerOrProjectSelect )
{
    var self = this;

    container = $(container);
    if( !onCustomerOrProjectSelect ) onCustomerOrProjectSelect = function() {};

    var initialized = false;
    var searchField = container.find(".combobox input");
    var cpDropdown = container.find(".cpDropdown");
    var cpDropdownOpenFromButton = false;
    var cpList = cpDropdown.find(".cpList");
    var expandAllButton = cpDropdown.find("button.expandAll");
    var collapseAllButton = cpDropdown.find("button.collapseAll");
    var spinner = new Spinner({ length: 3, width: 2, radius: 3, lines: 8, color: '#aaaaaa', trail: 60 });
    var selectedRow = $();
    var cpIndex = new CPIndex();
    var expandBackup;
    var searchMode = false;
    var hoverEnabled = new BoolTimer( true, 100 );
    var placeholderText = messageResource.getMessage("submit_tt.cp_selector.placeholder");


    /**
     * Initializes the selector with the given data.
     * @param customersProjectsInfo - customers & projects info, as defined in com.actimind.actitime.util.CustomersProjectsData.
     * @param isActive - whether to load active projects or archived
     */
    this.init = function( customersProjectsInfo, isActive )
    {
        if( initialized )
        {
            cpList.children( ".row" ).remove();
            cpList.children( ".projectsTrailer" ).remove();
            selectedRow = $();
            onCustomerOrProjectSelect( 0, 0, {}, {} );
            cpIndex = new CPIndex();
            searchMode = false;
        }
        var customerTemplate = cpList.find( ".templates .row.customer" );
        var projectTemplate = cpList.find( ".templates .row.project" );
        var projectsTrailerTemplate = cpList.find( ".templates .projectsTrailer" );

        $.each( customersProjectsInfo.customers, function ()
        {
            var customer = this;
            var customerRow = customerTemplate.clone().appendTo( cpList );

            customerRow.find( ".name" ).text( customer.name );
            customerRow.click( onCustomerRowClick );
            customerRow.find( "button.expandCollapse" )
                    .click( onExpandCollapseClick )
                    .mouseover( onExpandCollapseMouseOver );
            customerRow.mouseover( onRowMouseOver );

            var projectRows = [];

            $.each(customersProjectsInfo.projects, function() {
                if (this.customerId === customer.id && this.archived === !isActive) {
                    var projectRow = projectTemplate.clone().appendTo(cpList);

                    projectRow.find(".name").text(this.name);
                    projectRow.click(onProjectRowClick);
                    projectRow.mouseover(onRowMouseOver);

                    projectRow.data({
                        id: this.id,
                        customerRow: customerRow,
                        name: this.name,
                        fullName: customer.name + " / " + this.name,
                        customerId: this.customerId
                    });

                    projectRows.push(projectRow[0]);

                    cpIndex.addProject(projectRow);
                }
            });

            if( customer.archived === !isActive || projectRows.length > 0 )
            {
                var projectsTrailer = projectsTrailerTemplate.clone().appendTo( cpList ).hide();

                if( projectRows.length === 0 )
                    customerRow.addClass( "empty" );

                customerRow.data( { id: customer.id,
                                      name: customer.name,
                                      fullName: customer.name,
                                      projectRows: $( projectRows ),
                                      projectsTrailer: projectsTrailer
                                  } );

                cpIndex.addCustomer( customerRow );
            }
            else
            {
                customerRow.remove();
            }
        } );

        if( !isActive )
        {
            cpList.find( ".noEntriesMsg" ).text( messageResource.getMessage( "submit_tt.cp_selector.empty.archived" ) );
            cpList.find( ".noMatchesMsg" ).text( messageResource.getMessage( "submit_tt.cp_selector.nothing_found.archived" ) );
        }

        cpList.toggleClass("empty", cpList.children( ".row.customer" ).length == 0 );

        if( !initialized )
        {
            container.find( ".combobox .dropdownButton" ).click( onDropdownButtonClick );
            searchField.keydown( onSearchFieldKeyDown );
            searchField.focus( onSearchFieldFocus );
            expandAllButton.click( expandAll );
            collapseAllButton.click( collapseAll );
            setMouseDownOutsideHandler( container, onMouseDownOutside );
            cpList.scroll( onScroll );
        }

        setComboTextForSelectedRow();

        initialized = true;
    };

    /**
     * @return {Boolean} whether the selector is initialized or not.
     */
    this.isInitialized = function()
    {
        return initialized;
    };

    /**
     * Hides the selector and shows spinner instead.
     * TODO: since the selector itself doesn't load data from the server (that's when then spinner is shown),
     *       it is reasonable to move the spinning code outside of this module.
     */
    this.spin = function()
    {
        container.css("visibility", "hidden");
        spinner.spin( container.parent()[0] );
    };

    /**
     * Hides the spinner and shows the selector again.
     * TODO: see TODO in the 'spin' method.
     */
    this.stopSpin = function()
    {
        container.css("visibility", "visible");
        spinner.stop();
    };

    this.hideSelector = function()
    {
        var isSelected = selectedRow.length > 0;
        container.hide();
        if( isSelected )
        {
            selectedRow = $();
            setComboTextForSelectedRow();
            onCustomerOrProjectSelect( 0, 0, {}, {} );
        }
        spinner.stop();
    };

    this.showSelector = function()
    {
        container.show();
        spinner.stop();
    };

    /**
     * Closes the selector dropdown if it's open.
     */
    this.closeDropdown = function()
    {
        if(cpDropdownOpenFromButton){
            KeyDownHandler.restoreOnKeyDownHandler();
            cpDropdownOpenFromButton = false;
        }

        cpDropdown.addClass('hidden');
        disableSearchMode();
        setComboTextForSelectedRow();
        searchField.blur();
    };


    function CPIndex()
    {
        var map = {};
        var index = new StringIndex();

        /**
         * @param row jQuery object
         */
        this.addCustomer = function( row )
        {
            addRow( row, -row.data().id )
        };

        /**
         * @param row jQuery object
         */
        this.addProject = function( row )
        {
            addRow( row, row.data().id )
        };

        /**
         * @param string string
         * @return object {row: jQuery object, highlightedName: string}
         */
        this.find = function( string )
        {
            var tokens = index.getTokenizer().getTokens( string.toLowerCase() ); // TODO: tokenizer should use toLowerCase itself?
            var ids = index.search( tokens );
            if (tokens.length != 0 )
                var highlighter = new TokensHighlighter( tokens, index.getTokenizer().getPunctuators() );
            var results = [];

            $.each( ids, function(i) {
                results.push( {
                                  row: map[i],
                                  highlightedName: highlighter.highlightTokens( map[i].data().name )
                              } );
            });

            return results;
        };

        function addRow( row, id )
        {
            map[id] = row;
            index.add( row.data().name, id );
        }
    }


    function setComboTextForSelectedRow()
    {
        if( selectedRow.length == 1 )
        {
            searchField.val( selectedRow.data().fullName );
            searchField.removeClass("placeholder");
        }
        else
        {
            searchField.val( placeholderText );
            searchField.addClass("placeholder");
        }
    }

    function search( searchText )
    {
        if( !searchMode )
        {
            expandBackup = cpList.children(".row.customer.expanded");
            cpList.addClass("searchMode");
            searchMode = true;
        }

        collapseAll();
        cpList.children(".row.customer").hide();
        cpDropdown.removeClass('hidden');

        $.each( cpIndex.find( searchText ), function()
        {
            this.row.show();
            this.row.find(".name").html( this.highlightedName );
            if( this.row.hasClass("project") )
            {
                this.row.data().customerRow.show().addClass("expanded");
                this.row.data().customerRow.data().projectsTrailer.show();
            }
        });

        var visibleRows = cpList.children(".row:visible");

        if( visibleRows.length > 0)
        {
            cpList.removeClass("nothingFound");
            setActiveRow( visibleRows.first() );
        }
        else
        {
            cpList.addClass("nothingFound");
        }

        disableExpandAllCollapseAllButtons();
    }

    function disableSearchMode()
    {
        if( searchMode )
        {
            cpList.removeClass("searchMode");

            cpList.children(".row").find(".name").each( function() {
                $(this).text( $(this).text() );  // remove highlighting
            });

            cpList.children(".row.customer").show();

            collapseAll();

            expandBackup.each( function() {
                expandOrCollapseCustomer( $(this), true );
            });

            searchMode = false;

            updateExpandAllCollapseAllButtonsState();

            setActiveRow( null );
        }
    }

    function setActiveRow( row )
    {
        getActiveRow().removeClass("active");

        if( row )
        {
            row.addClass("active");
            row.scrollintoview( {duration: 0} );
        }
    }

    function getActiveRow()
    {
        return cpList.children(".row.active");
    }

    /**
     * @param customerRow jQuery object
     * @param [isExpand] boolean
     */
    function expandOrCollapseCustomer( customerRow, isExpand )
    {
        if( customerRow.is(":not(.empty)") )
        {
            customerRow.toggleClass( "expanded", isExpand );
            customerRow.data().projectRows.toggle( isExpand );
            customerRow.data().projectsTrailer.toggle( isExpand );
        }
    }

    function expandAll()
    {
        cpList.children(".row.customer").each( function() {
            expandOrCollapseCustomer( $(this), true );
        });
        updateExpandAllCollapseAllButtonsState();
    }

    function collapseAll()
    {
        cpList.children(".row.customer").each( function() {
            expandOrCollapseCustomer( $(this), false );
        });
        updateExpandAllCollapseAllButtonsState();
    }

    function disableExpandAllCollapseAllButtons()
    {
        expandAllButton.prop("disabled", true);
        collapseAllButton.prop("disabled", true);
    }

    function updateExpandAllCollapseAllButtonsState()
    {
        expandAllButton.prop("disabled", cpList.children(".row.customer:not(.expanded):not(.empty)").length == 0);
        collapseAllButton.prop("disabled", cpList.children(".row.customer.expanded").length == 0);
    }


    function onDropdownButtonClick()
    {
        disableSearchMode();
        setComboTextForSelectedRow();
        cpDropdown.toggleClass('hidden');
        if (!cpDropdown.hasClass('hidden')){
            KeyDownHandler.setOnKeyDownHandler( function (event) {if(event) event.stopPropagation(); onKeyDownDropdown(event); } );
            cpDropdownOpenFromButton = true;
        }
        else{
            if (cpDropdownOpenFromButton){
                KeyDownHandler.restoreOnKeyDownHandler();
            }
            cpDropdownOpenFromButton = false;
        }
        if( selectedRow.length == 1 )
        {
            if( selectedRow.is(".project") )
            {
                expandOrCollapseCustomer( selectedRow.data().customerRow, true );
            }

            setActiveRow( selectedRow );
        }

        updateExpandAllCollapseAllButtonsState();
    }

    function onCustomerRowClick()
    {
        selectedRow = $(this);
        self.closeDropdown();
        disableSearchMode();
        onCustomerOrProjectSelect( selectedRow.data().id, 0, selectedRow.data(), {});
    }

    function onProjectRowClick()
    {
        selectedRow = $(this);
        self.closeDropdown();
        disableSearchMode();
        onCustomerOrProjectSelect( 0, selectedRow.data().id, {}, selectedRow.data());
    }

    function onExpandCollapseClick()
    {
        var customerRow = $(this).parents(".row");

        expandOrCollapseCustomer( customerRow );
        updateExpandAllCollapseAllButtonsState();

        if( customerRow.hasClass("expanded") )
        {
            var trailer = customerRow.data().projectsTrailer;
            var trailerBottom = trailer.position().top + trailer.outerHeight( true );
            var customerTop = customerRow.position().top;
            var viewPortHeight = cpList.height();
            var scrollTop = cpList.scrollTop();

            if( trailerBottom > viewPortHeight )
            {
                var scrollTopDelta = (trailerBottom - customerTop < viewPortHeight) ?
                                     trailerBottom - viewPortHeight :
                                     customerTop;

                if( scrollTopDelta > 0 )
                    cpList.animate( { scrollTop: scrollTop + scrollTopDelta }, 300 );
            }
        }

        return false;
    }

    function onExpandCollapseMouseOver()
    {
        setActiveRow( null );
        return false;
    }

    function onRowMouseOver()
    {
        if( hoverEnabled.isTrue() )
            setActiveRow( $(this) );
    }

    function onSearchFieldKeyDown( event )
    {
        if( event.keyCode == KEY_CODE_UP || event.keyCode == KEY_CODE_DOWN )
        {
            var curActiveRow = getActiveRow();
            var newActiveRow;

            if( curActiveRow.length == 0 )
                newActiveRow = cpList.children(".row:visible").first();
            else
                newActiveRow = ( event.keyCode == KEY_CODE_UP ?
                                    curActiveRow.prevAll(".row:visible").first() :
                                    curActiveRow.nextAll(".row:visible").first() );

            if( newActiveRow.length == 1 )
                setActiveRow( newActiveRow );

            hoverEnabled.switchValue(); // don't react to mouse move while changing active row with keyboard

            event.preventDefault();
        }
        else if( event.keyCode == KEY_CODE_ENTER )
        {
            getActiveRow().click();
            event.preventDefault();  // prevent triggering the default button handler
        }
        else if( event.keyCode == KEY_CODE_ESC )
        {
            self.closeDropdown();
            event.preventDefault();
            event.stopPropagation();
        }
        else
        {
            var oldSearchText = $.trim( searchField.val() );

            setTimeout( function()
            {
                var searchText = $.trim( searchField.val() );

                if( searchText != oldSearchText )
                {
                    if( searchText != "" )
                        search( searchText );
                    else
                        disableSearchMode();
                }
            }, 1);
        }
    }

    function onSearchFieldFocus()
    {
        if( !searchMode )
        {
            searchField.val("");
            searchField.removeClass("placeholder");
        }
    }

    function onMouseDownOutside()
    {
        self.closeDropdown();
    }

    function onScroll()
    {
        hoverEnabled.switchValue(); // don't react to mouse move while scrolling
    }

    function onKeyDownDropdown( event )
    {
        event = event || window.event;
        if( event.keyCode == KEY_CODE_ESC )
        {
            self.closeDropdown();
        }
    }
}

export default CPSelectorController;


// WEBPACK FOOTER //
// ./web/js/user/add_tasks/cp_selector_controller.js